import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { either } from 'fp-ts';
import { pipe } from 'utils/fp';
import { listAllDatasetsTE } from 'api/datasets/listAllDatasets';
import { listModelsTE } from 'api/models/listModels';
import { listModulesTE } from 'api/modules';
import { listWorkflowsTE } from 'api/workflows';
import { apiErrorToReactLeft } from 'utils/errorHandling';
import { Entity } from 'utils/typeguards/cfEntities';
import { toJS } from 'mobx';
import { useDebounce } from 'react-use';
import { useLoggedInUserId } from 'modules/Auth/hooks';
import { modelFormApiEffects } from 'modules/Models/components/ModelForm/utils/apiEffects';
import { workflowPageApiEffects } from 'modules/Workflows/apiEffects';
import {
  IndexedDataType,
  MappedDataType,
  PlatformResourceSelectorDataStateType,
  SourceDataType,
} from 'components/PlatformResourceSelector/usePlatformResourceSelector';
import { isUrl } from 'utils/url';
import { useRouter } from 'next/router';

export type ResourceType = 'datasets' | 'models' | 'workflows' | 'modules' | 'models/workflows';
export type AllowedSectionsType = 'app-only' | 'community-only' | 'me-only';

export const allowedSections: Record<AllowedSectionsType, { label: (type: ResourceType) => string }> = {
  'app-only': { label: (type: ResourceType) => `This App's ${type}` },
  'community-only': { label: (type: ResourceType) => `Community ${type}` },
  'me-only': { label: (type: ResourceType) => `My ${type}` },
};

export type DataEntity = CF.API.Datasets.Dataset | CF.API.Models.Model | CF.API.Modules.Module | CF.API.Workflows.Workflow;

type SourceDataStateType = {
  indexed: IndexedDataType;
  labeled: SourceDataType;
};

type ActionType = {
  type: 'update';
  payload: SourceDataType;
};

const sourceDataReducer = (state: SourceDataStateType, action: ActionType): SourceDataStateType => {
  switch (action.type) {
    case 'update':
      return filterAndIndexDataEntities(state, action.payload);
    default:
      return state;
  }
};

const filterAndIndexDataEntities = (currentData: SourceDataStateType, data: SourceDataType): SourceDataStateType => {
  const updatedIndex = { ...currentData.indexed };
  const labeled: SourceDataType = {
    'app-only': [...currentData.labeled['app-only']],
    'me-only': [...currentData.labeled['me-only']],
    'community-only': [...currentData.labeled['community-only']],
  };

  Object.entries(data).forEach(([key, values]) => {
    const label = key as AllowedSectionsType;
    const entries = values?.length ? values : [];
    entries.forEach((entity) => {
      if (!updatedIndex[entity.id]) {
        updatedIndex[entity.id] = entity;
        labeled[label].push(entity);
      }
    });
  });

  return {
    indexed: updatedIndex,
    labeled,
  };
};

const initialSourceDataState: SourceDataStateType = {
  indexed: {},
  labeled: {
    'app-only': [],
    'me-only': [],
    'community-only': [],
  },
};

type UseDataFromResourceProps = {
  appContextUserId: string;
  appId: string;
  customFilter?: (item: DataEntity) => boolean;
  searchParam?: string;
  type: ResourceType;
};

export const useDataFromResource = ({
  appContextUserId,
  appId,
  customFilter,
  searchParam = '',
  type,
}: UseDataFromResourceProps): PlatformResourceSelectorDataStateType => {
  const loggedInUserId = useLoggedInUserId();

  const { userOrOrgId: currentOrg } = useRouter().query as { userOrOrgId: string };

  // Search mechanism
  // It monitors the value of the "searchParam" prop
  const [searchApiInput, setSearchApiInput] = useSearchMechanism('');
  // and debounces the search to avoid multiple renderings
  useDebounce(
    () => {
      if (searchParam.length) {
        if (countEntityResultsInData(searchParam, data.total) <= 0) {
          setSearchApiInput(searchParam);
        }
      } else {
        setSearchApiInput('');
      }
    },
    350,
    [searchParam],
  );

  // Data augmentation source and destination mechanism
  // Initialize the source data state and the reducer function to handle data updates
  const [sourceData, concatWithSourceData] = useReducer(sourceDataReducer, initialSourceDataState);
  // Memoize the data transformation to avoid unnecessary recalculations
  const data = useMemo(() => {
    const indexed: IndexedDataType = sourceData.indexed;
    const mapped: MappedDataType = Object.fromEntries(Object.entries(sourceData.labeled).filter(([_, items]) => items.length > 0));
    const total = Object.values(indexed);
    return {
      indexed,
      mapped,
      total,
    };
  }, [sourceData]);

  // Initial data and search from api
  // All api calls will be aborted if input search changes
  const [isLoading, setLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const userId = loggedInUserId !== appContextUserId ? loggedInUserId : appContextUserId;
    const searchQuery = searchApiInput.length ? `&search=${searchApiInput}` : '';

    // Abort the previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Create a new AbortController for the new request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (!searchQuery.length && data.total.length) return; // prevent searching when search query is empty and there are results already

    // NOTE: These calls pull from multiple contexts at once via params: (app (appId), user/org (userId), community (none))
    setLoading(true);

    fetchResourcesFromApi({ appContextUserId, appId, type, userId, searchQuery, abortSignal: abortControllerRef.current.signal, currentOrg })
      .then(([fromAppData, fromUserData, fromContextData, fromOrgData]) => {
        const appData = getRightResponse(fromAppData as ResourceApi, customFilter);
        const userData = getRightResponse(fromUserData as ResourceApi, customFilter);
        const contextData = fromContextData ? getRightResponse(fromContextData as ResourceApi, customFilter) : [];
        const orgData = fromOrgData ? getRightResponse(fromOrgData as ResourceApi, customFilter) : [];
        return concatWithSourceData({
          type: 'update',
          payload: {
            'app-only': appData,
            'me-only': [...userData, ...orgData],
            'community-only': contextData,
          },
        });
      })
      .finally(() => setLoading(false))
      .catch(() => {});
  }, [appId, appContextUserId, loggedInUserId, searchApiInput, type]);

  const addFromExternal = (entity: Entity) => {
    const externalItem = toJS(entity) as DataEntity;
    const itemExists = data.indexed[externalItem.id] || data.indexed[externalItem.id];
    if (itemExists) return;
    concatWithSourceData({
      type: 'update',
      payload: {
        'app-only': [],
        'me-only': [],
        'community-only': [externalItem],
      },
    });
  };

  return { type, data, isLoading, isSearching: searchApiInput.length > 0, addFromExternal };
};

type ResourceApi =
  | { datasets: CF.API.Datasets.Dataset[] }
  | { models: CF.API.Models.Model[] }
  | { workflows: CF.API.Workflows.Workflow[] }
  | { modules: CF.API.Modules.Module[] }
  | { 'models/workflows': (CF.API.Models.Model | CF.API.Workflows.Workflow)[] };
/**
 * Extracts the appropriate data array from the API response and optionally filters it.
 *
 * @param {ResourceApi} response - The API response containing one of the resource arrays.
 * @param {(item: DataEntity) => boolean} [customFilter] - Optional filter function to apply to the data array.
 * @returns {DataEntity[]} The extracted and optionally filtered data array.
 */
const getRightResponse = (response: ResourceApi, customFilter?: (item: DataEntity) => boolean): DataEntity[] => {
  let dataResponse: DataEntity[] = [];

  if ('datasets' in response) {
    dataResponse = response.datasets;
  } else if ('models' in response) {
    dataResponse = response.models;
  } else if ('modules' in response) {
    dataResponse = response.modules;
  } else if ('workflows' in response) {
    dataResponse = response.workflows;
  } else if ('models/workflows' in response) {
    dataResponse = response['models/workflows'];
  }

  if (customFilter) {
    return dataResponse.filter(customFilter);
  }

  return dataResponse;
};

/**
 * Extracts the version ID from a data entity.
 *
 * @param {DataEntity} item - The data entity from which to extract the version ID.
 * @returns {string} - The version ID of the data entity, or the entity's ID if no version ID is found.
 */
export const getVersionIdFromDataEntity = (item: DataEntity): string => {
  if ('module_version' in item) {
    return item.module_version?.id || item.id;
  } else if ('model_version' in item) {
    if (typeof item.model_version === 'string') return item.model_version as string; // in case model_version has the id and it's not an object
    return item.model_version?.id || item.id;
  }
  return item.version?.id || item.id;
};

/**
 * Generates a unique option value string from a given data entity.
 *
 * The generated string combines the `user_id`, `app_id`, and `id` of the entity,
 * separated by double underscores (`__`). This ensures that the option value is unique
 * and can be used reliably in selectors or dropdowns.
 *
 * @param {DataEntity | undefined} entity - The data entity from which to generate the option value.
 *                                          If no entity is provided, a default value of `'__'` is returned.
 * @returns {string} - A unique string representing the entity, or `'__'` if the entity is undefined.
 */
export const generateOptionValueFromEntity = (entity?: DataEntity): string => {
  if (!entity) return '';

  const type = getTypeFromDataEntity(entity);
  if (!type) return '';

  // TODO: add support for other types

  if (type === 'models') return `${entity.id}__${getVersionIdFromDataEntity(entity)}`;
  return entity.id;
};

/**
 * Extracts the type ID from a data entity.
 *
 * @param {DataEntity} item - The data entity from which to extract the type ID.
 * @returns {ResourceType} - The type ID of the data entity.
 */
export const getTypeFromDataEntity = (item: DataEntity): ResourceType => {
  if ('model_version' in item) {
    return 'models';
  } else if ('module_version' in item) {
    return 'modules';
  } else if ('nodes' in item) {
    return 'workflows';
  } else if (!('nodes' in item) && 'version' in item) {
    return 'datasets';
  }
  return 'models/workflows';
};

/**
 * Searches for entities in the provided data that match the search string.
 *
 * @param {string} search - The search string to look for in the entity IDs.
 * @param {DataEntity[]} data - The array of data entities to search within.
 * @returns {number} - The filtered array count of entities that match the search string.
 */
export const countEntityResultsInData = (searchTerm: string, data: DataEntity[]): number => {
  if (isUrl(searchTerm)) {
    // WARNING!!
    // only works for models urls
    const { modelId, modelVersionId } = extractModelIdsFromUrl(searchTerm);
    const filteredData = data.filter((entity) => {
      if (modelVersionId) return getVersionIdFromDataEntity(entity).includes(modelVersionId);
      if (modelId) return entity.id.includes(modelId);
      return false;
    });
    // Return the filtered data count if any matches are found.
    return filteredData.length;
  }

  const filteredData = data.filter((entity) => {
    return (
      entity.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getVersionIdFromDataEntity(entity).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });
  // Return the filtered data count if any matches are found.
  return filteredData.length;
};

// NOTE: constants controls how many resources and parameters we fetch from each endpoint, can be adjusted as needed:
const PAGES_OF_RESULTS = 1;
const RESULTS_PER_PAGE = 100;
const paginationQueryParam = `page=${PAGES_OF_RESULTS}&per_page=${RESULTS_PER_PAGE}`;
const trainedOnlyQueryParam = `&trained_only=true`;
const additionalFieldsQueryParam = '&sort_by_modified_at=true&additional_fields=stars';
const dontFetchFromMainQueryParam = '&dont_fetch_from_main=true';

type FetchResourcesFromApiProps = {
  abortSignal: AbortSignal;
  appContextUserId: string;
  appId: string;
  searchQuery: string;
  type: ResourceType;
  userId: string;
  currentOrg: string | undefined; // Optional, needed for models
};
/**
 * Fetches resources from the API based on the provided parameters.
 *
 * This function supports fetching datasets, models, workflows, and modules.
 * Depending on the type of resource and the user context, it makes API calls
 * to retrieve the relevant data.
 *
 * @param {Object} props - The properties for fetching resources.
 * @param {string} props.appContextUserId - The user ID in the app context.
 * @param {string} props.appId - The application ID.
 * @param {string} props.searchQuery - The search query string.
 * @param {string} props.type - The type of resource to fetch (e.g., 'datasets', 'models', 'workflows', 'modules').
 * @param {string} props.currentOrg - The current organization ID, if applicable. Used for models.
 * @param {string} props.userId - The user ID.
 * @returns {Promise} A promise that resolves to the fetched resources. Correspond to ResourceApi Type
 */
const fetchResourcesFromApi = ({ abortSignal, appContextUserId, appId, searchQuery = '', type, userId, currentOrg }: FetchResourcesFromApiProps) => {
  if (type === 'datasets' && appContextUserId) {
    return Promise.all([
      listDatasetsApi({ abortSignal, userId: appContextUserId, appId, params: `${paginationQueryParam}${searchQuery}` }),
      listDatasetsApi({ abortSignal, userId, params: `${paginationQueryParam}${searchQuery}${additionalFieldsQueryParam}` }),
    ]);
  } else if (type === 'models') {
    const listModelsRequests = [
      listModelsApi({
        abortSignal,
        userId: appContextUserId,
        appId,
        params: `${paginationQueryParam}${dontFetchFromMainQueryParam}${searchQuery}${trainedOnlyQueryParam}`,
      }),
      listModelsApi({ abortSignal, userId, params: `${paginationQueryParam}${searchQuery}${trainedOnlyQueryParam}${additionalFieldsQueryParam}` }),
      // Always return compatible community results
      Promise.all([
        listModelsApi({
          abortSignal,
          params: `${paginationQueryParam}${additionalFieldsQueryParam}${searchQuery}${trainedOnlyQueryParam}`,
        }),
      ]).then(([fromModelsData]) => {
        return { models: [...fromModelsData.models] };
      }),
    ];
    if (currentOrg) {
      listModelsRequests.push(
        listModelsApi({
          abortSignal,
          userId: currentOrg,
          params: `${paginationQueryParam}${searchQuery}${trainedOnlyQueryParam}${additionalFieldsQueryParam}`,
        }),
      );
    }
    return Promise.all(listModelsRequests);
  } else if (type === 'workflows') {
    return Promise.all([
      listWorkflowsApi({ abortSignal, userId: appContextUserId, appId, params: `${paginationQueryParam}${searchQuery}` }),
      listWorkflowsApi({ abortSignal, userId, params: `${paginationQueryParam}${searchQuery}${additionalFieldsQueryParam}` }),
    ]);
  } else if (type === 'modules') {
    return Promise.all([
      listModulesApi({ abortSignal, userId: appContextUserId, appId, params: `${paginationQueryParam}${searchQuery}` }),
      listModulesApi({ abortSignal, userId, params: `${paginationQueryParam}${additionalFieldsQueryParam}` }),
    ]);
  } else {
    // models/workflows, etc.
    return Promise.all([
      Promise.all([
        listModelsApi({
          abortSignal,
          userId: appContextUserId,
          appId,
          params: `${paginationQueryParam}${dontFetchFromMainQueryParam}${searchQuery}${trainedOnlyQueryParam}`,
        }),
        listWorkflowsApi({
          abortSignal,
          userId: appContextUserId,
          appId,
          params: `${paginationQueryParam}${additionalFieldsQueryParam}${searchQuery}`,
        }),
      ]).then(([fromModelsData, fromWorkflowsData]) => {
        return { 'models/workflows': [...fromModelsData.models, ...fromWorkflowsData.workflows] };
      }),
      Promise.all([
        listModelsApi({
          abortSignal,
          userId,
          params: `${paginationQueryParam}${additionalFieldsQueryParam}${searchQuery}${trainedOnlyQueryParam}`,
        }),
        listWorkflowsApi({ abortSignal, userId, params: `${paginationQueryParam}${additionalFieldsQueryParam}${searchQuery}` }),
      ]).then(([fromModelsData, fromWorkflowsData]) => {
        return { 'models/workflows': [...fromModelsData.models, ...fromWorkflowsData.workflows] };
      }),
      // NOTE: search may conditionally be targeting a different org or user context
      // if search is not needed, use undefined as value instead
      searchQuery.length
        ? Promise.all([
            listModelsApi({ abortSignal, params: `${paginationQueryParam}${additionalFieldsQueryParam}${searchQuery}${trainedOnlyQueryParam}` }),
            listWorkflowsApi({ abortSignal, params: `${paginationQueryParam}${additionalFieldsQueryParam}${searchQuery}` }),
          ]).then(([fromModelsData, fromWorkflowsData]) => {
            return { 'models/workflows': [...fromModelsData.models, ...fromWorkflowsData.workflows] };
          })
        : undefined,
    ]);
  }
};

type ListApiProps = {
  abortSignal: AbortSignal;
  appId?: string;
  params: string;
  userId?: string;
};
/**
 * Fetches a list of datasets from the API.
 *
 * @param {ListApiProps} props - The properties for the API call.
 * @returns {Promise<{ datasets: CF.API.Datasets.Dataset[] }>} A promise that resolves to the list of datasets.
 */
const listDatasetsApi = ({ abortSignal, appId, params, userId }: ListApiProps): Promise<{ datasets: CF.API.Datasets.Dataset[] }> => {
  return listAllDatasetsTE({ abortSignal, appId, params, userId }, apiErrorToReactLeft)().then((response) => {
    return { datasets: 'right' in response ? response.right.datasets : [] };
  });
};
/**
 * Fetches a list of models from the API.
 *
 * @param {ListApiProps} props - The properties for the API call.
 * @returns {Promise<{ models: CF.API.Models.Model[] }>} A promise that resolves to the list of models.
 */
const listModelsApi = ({ abortSignal, appId, params, userId }: ListApiProps): Promise<{ models: CF.API.Models.Model[] }> => {
  return listModelsTE({ abortSignal, appId, params, userId }, apiErrorToReactLeft)().then((response) => {
    return { models: 'right' in response ? response.right.models : [] };
  });
};
/**
 * Fetches a list of workflows from the API.
 *
 * @param {ListApiProps} props - The properties for the API call.
 * @returns {Promise<{ workflows: CF.API.Workflows.Workflow[] }>} A promise that resolves to the list of workflows.
 */
const listWorkflowsApi = ({ abortSignal, appId, params, userId }: ListApiProps): Promise<{ workflows: CF.API.Workflows.Workflow[] }> => {
  return listWorkflowsTE({ abortSignal, appId, params, userId }, apiErrorToReactLeft)().then((response) => {
    return { workflows: 'right' in response ? response.right.workflows : [] };
  });
};
/**
 * Fetches a list of modules from the API.
 *
 * @param {ListApiProps} props - The properties for the API call.
 * @returns {Promise<{ modules: CF.API.Modules.Module[] }>} A promise that resolves to the list of modules.
 */
const listModulesApi = ({ abortSignal, appId, params, userId }: ListApiProps): Promise<{ modules: CF.API.Modules.Module[] }> => {
  return listModulesTE({ abortSignal, appId, params, userId }, apiErrorToReactLeft)().then((response) => {
    return { modules: 'right' in response ? response.right.modules : [] };
  });
};

export const useEmbedModelsFromApp = ({ appId, userId }: { appId: string; userId: string }): PlatformResourceSelectorDataStateType => {
  const [sourceEmbedModels, setSourceEmbedModels] = useState<EmbedModelDataType[]>([]);

  useEffect(() => {
    fetchEmbedModels(userId as string, appId as string)
      .then((data) => setSourceEmbedModels(data))
      .catch(() => []);
  }, [appId, userId]);

  const data = useMemo(() => {
    const indexed: IndexedDataType = sourceEmbedModels.reduce((accum, node) => {
      const index = `${node.id}__${node.model_version}`;
      if (!accum[index]) {
        accum[index] = node as unknown as DataEntity; // forcing data type to fill data with compatible props
      }
      return accum;
    }, {} as IndexedDataType);

    const mapped: MappedDataType = {
      ...(Object.values(indexed).length ? { 'app-only': Object.values(indexed) } : {}),
    };

    const total = Object.values(indexed);

    return {
      indexed,
      mapped,
      total,
    };
  }, [sourceEmbedModels]);

  return {
    type: 'models',
    data,
    isLoading: false,
  };
};

type EmbedModelDataType = {
  id: string;
  model_id: string;
  model_type_id: string;
  model_type_title: string;
  model_version: string;
  name: string;
};

const fetchEmbedModels = async (userId: string, appId: string): Promise<EmbedModelDataType[]> => {
  const appE = await modelFormApiEffects.fetchAppTE(appId, userId)();
  const baseWorkflowId = pipe(
    appE,
    either.fold(
      () => undefined,
      (app) => app.default_workflow?.id,
    ),
  );
  // GET the default worflow from the workflows api using the default workflow id
  const defaultWorkflowE = await workflowPageApiEffects.fetchWorkflowTE(userId, appId, baseWorkflowId as string)();
  // GET all the models types available
  const modelTypesE = await modelFormApiEffects.fetchModelTypeFieldsTE()();
  if (!either.isRight(modelTypesE)) {
    return [];
  }
  // MAP all model types and create a reduced key/value pair
  // key=modelId, value={ title: modelTitle; output: string[] | null | undefined }
  const modelTypes: Record<string, { title: string; output: string[] | null | undefined }> = pipe(
    modelTypesE,
    either.fold(
      () => ({} as Record<string, { title: string; output: string[] | null | undefined }>),
      (models) =>
        models.reduce((modelsMap, model) => {
          modelsMap[model.id] = { title: model.title, output: model.output_fields };
          return modelsMap;
        }, {} as Record<string, { title: string; output: string[] | null | undefined }>),
    ),
  );
  // LIST of models
  // it goes throw the default workflow nodes, each node is a model
  // filters nodes by output.includes('embeddings')
  const workflowNodeVersionIds: EmbedModelDataType[] = pipe(
    defaultWorkflowE,
    either.fold(
      () => [],
      (defaultWorkflow) => {
        return (
          defaultWorkflow?.nodes
            ?.filter((node) => modelTypes[node.model.model_type_id] && modelTypes[node.model.model_type_id].output?.includes('embeddings'))
            .map((node) => {
              return {
                id: node.model.id,
                model_id: node.model.id,
                model_type_id: node.model.model_type_id,
                model_type_title: modelTypes[node.model.model_type_id].title,
                model_version: node.model.model_version.id,
                name: modelTypes[node.model.model_type_id].title,
              };
            }) || []
        );
      },
    ),
  );

  return workflowNodeVersionIds;
};

export const extractModelIdsFromUrl = (url: string): { modelId?: string; modelVersionId?: string } => {
  const regex = /\/models\/([^/]+)(?:\/versions\/([^/]+))?/;
  const match = url.match(regex);

  if (match) {
    const modelId = match[1];
    const modelVersionId = match[2];
    return { modelId, modelVersionId };
  }

  return { modelId: undefined, modelVersionId: undefined };
};

/**
 * Custom hook for managing a search mechanism.
 *
 * This hook maintains a search state and provides a setter function to update it.
 * If the input is a URL, it extracts the `modelId` from the URL and sets the state with it.
 * If the input is not a URL, it directly sets the state with the provided string.
 *
 * @param {string} input - The initial value for the search state.
 * @returns {[string, (searchTerm: string) => void]} - An array containing the current search state
 *                                                    and a setter function to update the state.
 */
export const useSearchMechanism = (input: string): [string, (searchTerm: string) => void] => {
  const [searchState, setSearchState] = useState<string>(input);

  const setSearch = (searchTerm: string) => {
    if (isUrl(searchTerm)) {
      const { modelId } = extractModelIdsFromUrl(searchTerm);
      if (modelId) return setSearchState(modelId);
    }
    return setSearchState(searchTerm);
  };

  return [searchState, setSearch];
};
